import { Invite, InviteType, User, UserRole } from '@cc/schema';
import { rawErrorMessage } from './normalizeDataError';
import { AuthProvider } from '@refinedev/core';
import * as Sentry from '@sentry/react';
import { formatISO } from 'date-fns';
import * as fbAuth from 'firebase/auth';
import { Auth, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { gqlDataProvider } from '../App';
import { updateHeaderToken } from './authProvider';
import { LOCAL_STORAGE_KEYS } from '../helpers';
import i18n from '../i18n';
import type { AuthBindings } from '@refinedev/core';

// not exported from the package root (its `exports` map blocks the deep path) — derived from the public AuthBindings
type AuthActionResponse = Awaited<ReturnType<AuthBindings['login']>>;

export declare interface ILoginArgs {
  provider: 'email' | 'gmail';
  email?: string;
  password?: string;
  remember?: boolean;
  loginAfterRegister?: boolean;
  loginFunc?: () => void;
}

export declare interface IRegisterArgs extends ILoginArgs {}

declare interface IAuthCallbacks {
  onRegister?: (user: fbAuth.User) => void;
  onLogin?: (user: fbAuth.User) => void;
  onLogout?: (auth: fbAuth.Auth) => any;
}

export declare interface IUpdatePasswordArgs {
  actionCode: string;
  password: string;
}

const getDbUser = async (user: fbAuth.User) => {
  const res = await gqlDataProvider.getOne<User>({
    resource: 'practiceUsers',
    id: user.uid, // Note: id is not used on BE
    meta: {
      fields: [
        '_id',
        'firstName',
        'lastName',
        'email',
        'phone',
        'extension',
        'country',
        'practiceId',
        'displayName',
        'role',
        'accessLevel',
        'diagnoses',
        {
          featureFlags: [
            { cardsSearch: ['name', 'count'] },
            { articlesView: ['name', 'count'] },
            { statsView: ['name', 'count'] },
            { locationReminders: ['name', 'count'] },
            { dayReminders: ['name', 'count'] },
            { phq9: ['name', 'count'] },
            { gad7: ['name', 'count'] },
            { meditations: ['name', 'count'] },
            { worryTrees: ['name', 'count'] },
          ],
        },
        {
          privacySettings: [
            'isPrivacyAccepted',
            'isTermsAccepted',
            'isProductEmailNotifyOn',
            'isProductPushNotifyOn',
            'isErrorInfoCollectionOn',
            'isGAUsageCollectionOn',
            'isKeepLoggedInOn',
          ],
        },
        {
          practice: [
            '_id',
            {
              subscriptionWarnings: [
                'subscriptionExpired',
                'subscriptionExpirationDate',
                'clientsPurchased',
                'clientsAdded',
                'limitReached',
                'limitApproaching',
                'clientsLeft',
                'evaluatedAt',
              ],
            },
            {
              subscription: [
                'stripeCustomerId',
                'quantity',
                'currentPeriodStart',
                'currentPeriodEnd',
                'cancelAtPeriodEnd',
                'status',
                'freeAppsUsed',
                'downgradeWarningSeenAt',
                'currentProductId',
                'currentPriceId',
                {
                  subscriptions: [
                    'id',
                    'status',
                    'currentPeriodEnd',
                    'currentPeriodStart',
                    'canceledAt',
                    'cancelAtPeriodEnd',
                    'latestInvoiceId',
                    {
                      plan: ['productId', 'priceId', 'quantity'],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  });

  return res;
};

const saveUser = async (user: fbAuth.User) => {
  /**
   * F-205 (founder Q-f204-1 A, 2026-09-30): `createUser` answers an EXISTING account's row only to
   * the account whose token the request carries. The header is otherwise set by `onAuthStateChanged`
   * on its own schedule, which can lose the race to a sign-up or a Google sign-in that lands here
   * right after Firebase answers — so the new account's token goes on the request first.
   */
  await updateHeaderToken();
  const inviteToken = localStorage?.getItem(LOCAL_STORAGE_KEYS.inviteToken);

  const utmData = localStorage?.getItem(LOCAL_STORAGE_KEYS.utmData);

  const createdAt = user.metadata.creationTime
    ? new Date(user.metadata.creationTime)
    : new Date();

  const newUser = {
    createdAt: formatISO(createdAt),
    displayName: user.displayName,
    email: user.email,
    lng: i18n.languages[0],
    photoUrl: user.photoURL,
    providerId: user.providerId,
    role: UserRole.THERAPIST_ADMIN,
    uid: user.uid,
    updatedAt: formatISO(Date.now()),
    timezone: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone,
    inviteToken,
    utmData,
  };

  // The invite token stays in storage until the account exists: a PRACTICE invite is the wizard's,
  // not the API's (F-193) — see `dropSpentInviteToken` below.
  if (utmData) {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.utmData);
  }

  const data = {
    url: '',
    method: 'post' as const,
    meta: {
      operation: 'createUser',
      fields: [{ user: ['_id'] }],
      variables: {
        user: {
          name: 'user',
          type: 'UserInput!',
          value: newUser,
        },
      },
    },
  };
  const created = await gqlDataProvider.custom<User>(data);
  if (inviteToken) {
    await dropSpentInviteToken(inviteToken);
  }
  return created;
};

/**
 * F-193 (founder Q-b4r2-2 A, 2026-09-30). A sign-up used to clear the saved invite token before the
 * setup wizard could read it, so a colleague invited to a practice who SIGNED UP (rather than in) met
 * an empty practice-code field. `createUser` spends only a THERAPIST-type token (a client's invitation
 * — the grant co-event); a PRACTICE_USER / PRACTICE_ADMIN token is the wizard's (E03 finding 12: "the
 * code kept for the wizard"), so it stays. The type is read from the API now that the account exists
 * (`inviteByToken` answers only a signed-in session); when it cannot be confirmed as a practice token
 * the old behaviour holds and the key is cleared.
 */
const dropSpentInviteToken = async (token: string) => {
  let keep = false;
  try {
    const res = await gqlDataProvider.custom<Invite>({
      url: '',
      method: 'get' as const,
      meta: {
        operation: 'inviteByToken',
        fields: ['_id', 'type'],
        variables: { token: { name: 'token', type: 'String', value: token } },
      },
    });
    keep = [InviteType.PRACTICE_USER, InviteType.PRACTICE_ADMIN].includes(
      res?.data?.type as InviteType,
    );
  } catch {
    keep = false;
  }
  if (!keep) {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.inviteToken);
  }
};

let cachedDbUser: User | null = null;
let authRetries = 0;

export const resetCachedDbUser = () => {
  cachedDbUser = null;
};

/**
 * Resolves at Firebase's FIRST auth-state callback for this Auth — the persisted session restored, or none
 * (firebase 9 has no `auth.authStateReady()`; its first `onAuthStateChanged` call is the same signal). One
 * promise per Auth instance: after the first answer every check is immediate.
 */
const authStateKnownByAuth = new WeakMap<Auth, Promise<void>>();
const authStateKnown = (auth: Auth): Promise<void> => {
  let known = authStateKnownByAuth.get(auth);
  if (!known) {
    known = new Promise<void>((resolve) => {
      const stop = fbAuth.onAuthStateChanged(auth, () => {
        stop();
        resolve();
      });
    });
    authStateKnownByAuth.set(auth, known);
  }
  return known;
};

export const getAuthProvider = (
  auth: Auth,
  authActions?: IAuthCallbacks,
): AuthProvider => {
  const login = async (
    params = {} as ILoginArgs,
  ): Promise<AuthActionResponse> => {
    const { provider, email, password, remember, loginAfterRegister } = params;

    try {
      if (loginAfterRegister) {
        return {
          success: true,
          redirectTo: '/',
        };
      }
      if (provider === 'email' && email && password) {
        if (auth) {
          await auth.setPersistence(
            remember
              ? fbAuth.browserLocalPersistence
              : fbAuth.browserSessionPersistence,
          );

          const userCredential = await fbAuth.signInWithEmailAndPassword(
            auth,
            email,
            password,
          );
          const userToken = await userCredential?.user?.getIdToken?.();

          if (userToken) {
            await authActions?.onLogin?.(userCredential.user);
            await getIdentity();

            return {
              success: true,
              redirectTo: '/',
            };
          } else {
            return {
              success: false,
              error: new Error('No token'),
            };
          }
        } else {
          return {
            success: false,
            error: new Error('No auth instance'),
          };
        }
      } else {
        if (auth) {
          await auth.setPersistence(
            remember
              ? fbAuth.browserLocalPersistence
              : fbAuth.browserSessionPersistence,
          );

          const provider = new GoogleAuthProvider();

          try {
            const res = await signInWithPopup(auth, provider);
            const userCredential = GoogleAuthProvider.credentialFromResult(res);
            const userToken = userCredential?.accessToken;
            const user = res.user;

            try {
              const dbUser = await getDbUser(user);
              if (!dbUser) {
                await saveUser(user);
              }
            } catch (e) {
              if (
                [
                  'У вас нет разрешения на получение',
                  'You do not have permissions',
                ].some((s) => rawErrorMessage(e).includes(s))
              ) {
                await saveUser(user);
              }
            }

            if (userToken) {
              await authActions?.onLogin?.(user);
              // await getUserIdentity(); // TODO: see if needed

              return {
                success: true,
                redirectTo: '/',
              };
            } else {
              return {
                success: false,
                error: new Error('No token'),
              };
            }
          } catch (error) {
            return {
              success: false,
              error: error as Error,
            };
          }
        } else {
          return {
            success: false,
            error: new Error('No auth instance'),
          };
        }
      }
    } catch (error) {
      return {
        success: false,
        error: error as Error,
      };
    }
  };

  const logout = async () => {
    try {
      await fbAuth.signOut(auth);
      resetCachedDbUser();
      localStorage.removeItem(LOCAL_STORAGE_KEYS.clientAccountWarningShown);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.utmData);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.inviteToken);
      await authActions?.onLogout?.(auth);
      return {
        success: true,
        redirectTo: '/login',
      };
    } catch (error) {
      return {
        success: true,
        error: error as Error,
      };
    }
  };

  // TODO: modify fonnsumers of former getUserIdentity
  const getIdentity = async () => {
    const user = auth?.currentUser;

    if (user) {
      let dbUser = cachedDbUser;
      if (!dbUser) {
        const res = await getDbUser(user);
        dbUser = res.data;
        cachedDbUser = { ...(res.data || {}) };
      }

      // TODO: implement later
      // if (!this.loginInfo && !this.loginInfoRequested) {
      //   this.loginInfoRequested = true;

      //   try {
      //     // TODO: think of using https://ipregistry.co/ since they have more data
      //     const res = await fetch('https://ipapi.co/json/');
      //     const data = await res.json();

      //     if (data) {
      //       this.loginInfo = {
      //         ip: data.ip,
      //         city: data.city,
      //         region: data.region,
      //         regionCode: data.region_code,
      //         countryCode: data.country_code,
      //         countryCallingCode: data.country_calling_code,
      //         zip: data.postal,
      //         timezone: data.timezone,
      //       };
      //     }
      //   } catch (error) {}
      // }

      return {
        fb: { ...auth.currentUser },
        // @ts-ignore
        db: { ...dbUser },
        // loginInfo: loginInfo,
        loginInfo: {} as any,
      };
    }
    return null;
  };

  const onError = async (error: any) => {
    if (error) {
    }
    if (rawErrorMessage(error).includes('auth/id-token-expired')) {
      if (authRetries < 2) {
        authRetries++;

        const refreshToken = async () => {
          try {
            const newToken = await auth.currentUser?.getIdToken(true);
            const res = await updateHeaderToken();
            authRetries = 0;

            return {};
          } catch (e) {
            console.log('token refresh e', e);
          }
        };

        refreshToken();
      } else {
        authRetries = 0;

        return {
          redirectTo: '/login',
          logout: true,
          error: new Error('Token expired or invalid'), // TODO: translate
        };
      }
    }
    if (error.status === 401) {
      return {
        redirectTo: '/login',
        logout: true,
        error, // TODO: translate
      };
    }
    return {};
  };

  const check = async () => {
    // Wait until Firebase has RESTORED the persisted session, never a fixed delay (F-61, 2026-09-28): the old
    // 100 + 200 ms guess lost the race whenever the restore (a round trip to Google) took longer — on a slow
    // connection every full page load; a deep link (`/sessions/create?clientId=…`) went to /login and on to
    // /clients, the page the therapist asked for lost. Found by the E34 Playwright row 13; on a plane's Wi-Fi
    // the unchanged routing spec went 23 red of 25.
    await authStateKnown(auth);
    if (auth?.currentUser) {
      return {
        authenticated: true,
      };
    } else {
      return {
        authenticated: false,
        logout: true,
        redirect: '/login',
        // TODO: translate?
        error: {
          message: 'Check failed',
          name: 'Unauthorized',
        },
      };
    }
  };

  // Note: optional and is not currently used
  // const getPermissions = async () => {
  //   debugger;
  //   if (auth?.currentUser) {
  //     var idTokenResult = await fbAuth.getIdTokenResult(auth.currentUser);
  //     return idTokenResult?.claims;
  //   } else {
  //     return Promise.reject('User is not found in getPermissions');
  //   }
  // }

  const register = async (args = {} as IRegisterArgs) => {
    try {
      const { provider, email, password, loginFunc } = args;

      if (provider === 'email' && email && password) {
        const userCredential = await fbAuth.createUserWithEmailAndPassword(
          auth,
          email,
          password,
        );

        // const actionCodeSettings = {
        //   handleCodeInApp: false,
        //   url: 'https://portal.copingcard.com',
        //   dynamicLinkDomain: 'portal.copingcard.com',
        // };

        // await fbAuth.sendEmailVerification(
        //   userCredential.user,
        //   actionCodeSettings,
        // );

        const { user } = userCredential;

        if (user) {
          await saveUser(user);
          authActions?.onRegister?.(user);
          resetCachedDbUser();
          loginFunc?.();
          return {
            success: true,
          };
        }
      } else {
        const provider = new GoogleAuthProvider();

        // TODO: add
        // this.auth.languageCode = lng

        try {
          const res = await signInWithPopup(auth, provider);
          const userCredential = GoogleAuthProvider.credentialFromResult(res);
          const user = res.user;
          const userToken = userCredential?.accessToken;
          // The signed-in user info.

          if (user) {
            await saveUser(user);
            await authActions?.onRegister?.(user);
            resetCachedDbUser();
            loginFunc?.();
            return {
              success: true,
            };
          }
        } catch (error) {
          return {
            success: false,
            error: error as Error,
          };
        }
      }
      return {
        success: false,
      };
    } catch (error) {
      return {
        success: false,
        error: error as Error,
      };
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      // Note: keeping for reference, thought currently it does not allow customizing the URL
      // fbAuth.sendPasswordResetEmail(this.auth, email);

      const res = await gqlDataProvider.custom<User>({
        url: '',
        method: 'post',
        meta: {
          operation: 'resetPracticeUserPassword',
          fields: ['success'],
          variables: {
            input: {
              name: 'input',
              type: 'PracticeResetUserPasswordInput!',
              value: {
                data: email,
              },
            },
          },
        },
      });

      resetCachedDbUser();
      return {
        success: true,
      };
    } catch (e) {
      Sentry.captureException(e);
      return {
        success: false,
        error: e as Error,
      };
    }
    return {
      success: true,
    };
  };

  const updatePassword = async (params = {} as IUpdatePasswordArgs) => {
    const { actionCode, password } = params;
    try {
      const email = await fbAuth.verifyPasswordResetCode(auth, actionCode);

      if (!email) {
        return {
          success: false,
        };
      }

      await fbAuth.confirmPasswordReset(auth, actionCode, password);

      resetCachedDbUser();

      return {
        success: false,
      };
    } catch (e) {
      Sentry.captureException(e);
      return {
        success: false,
        error: e as Error,
      };
    }
  };
  return {
    login,
    logout,
    getIdentity,
    onError,
    check,
    register,
    forgotPassword,
    updatePassword,
    // getPermissions,
  };
};
